import React from "react";
import { Expand, TextField } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import CrossReverse from "public/cross-reverse.svg";
import ButtonV2 from "elements/ButtonV2";
import FileUpload, { FileUploadType } from "elements/Input/FileUpload";
import client from "utils/client";

const VehicleRegistrations = () => {
	const { flowId, next, specialFieldsValue, setSpecialFieldsValue } =
		useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const [registrationFiles, setRegistrationFiles] = React.useState<
		FileUploadType[]
	>([]);

	const vehicleRegistrationsObjectToUse = React.useMemo(() => {
		return specialFieldsValue?.vehicle_registrations &&
			specialFieldsValue?.vehicle_registrations?.length > 0
			? specialFieldsValue?.vehicle_registrations
			: [{ registration_id: "", county: "" }];
	}, [specialFieldsValue]);

	const buttonDisabled = React.useMemo(() => {
		return vehicleRegistrationsObjectToUse.some(vehicleRegistration => {
			if (flowId === "GEORGIA_EXEMPTION") {
				if (details?.county === "Fulton") {
					return !registrationFiles.length;
				}

				return (
					vehicleRegistration.registration_id?.length < 5 ||
					vehicleRegistration.county == ""
				);
			}

			if (flowId === "FLORIDA_EXEMPTION") {
				return vehicleRegistration.registration_id?.length < 5;
			}

			return true;
		});
	}, [vehicleRegistrationsObjectToUse, registrationFiles]);

	const addAnotherVehicleRegistration = () =>
		setSpecialFieldsValue({
			vehicle_registrations: [
				...vehicleRegistrationsObjectToUse,
				{ registration_id: "", county: "" },
			],
		});
	const removeVehicleRegistration = (index: number) =>
		setSpecialFieldsValue({
			vehicle_registrations: vehicleRegistrationsObjectToUse.filter(
				(_, i) => i !== index
			),
		});

	const updateVehicleRegistration = (
		index: number,
		values: Partial<{ registration_id: string; county: string }>
	) => {
		const updatedVehicleRegistrations = vehicleRegistrationsObjectToUse.map(
			(item, i) => {
				if (i === index) {
					return {
						...item,
						...values,
					};
				}
				return item;
			}
		);
		setSpecialFieldsValue({
			vehicle_registrations: updatedVehicleRegistrations,
		});
	};

	const skipVehicleRegistration = () => {
		setSpecialFieldsValue({ vehicle_registrations: [] });
		next();
	};

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_VEHICLE_REGISTRATIONS_Answer,
			data: {
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});

		if (flowId === "GEORGIA_EXEMPTION" && details?.county === "Fulton") {
			const fd = new FormData();

			registrationFiles.forEach(file => {
				fd.append("vehicleRegistration", file.file);
			});

			await client.uploadExemptionFiles({
				formData: fd,
				propertyId: details.id,
			});
		} else {
			setSpecialFieldsValue({
				vehicle_registrations:
					specialFieldsValue?.vehicle_registrations?.filter(
						vehicleRegistration =>
							vehicleRegistration.registration_id !== "" &&
							vehicleRegistration.county !== ""
					),
			});
		}

		next();
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Vehicle Registration</p>
				</div>
				{flowId === "GEORGIA_EXEMPTION" && (
					<p className="exemption-question-card-subtitle sm">
						The county requests{" "}
						{details?.county === "Fulton"
							? "a copy of your registration"
							: "information"}{" "}
						for all vehicles owned.
					</p>
				)}

				{flowId === "FLORIDA_EXEMPTION" && (
					<p className="exemption-question-card-subtitle sm">
						The county requires this information in order to approve the
						application.
					</p>
				)}

				{details?.state === "GA" && details.county === "Fulton" ? (
					<div className="mt-1">
						<FileUpload
							filesToUpload={registrationFiles}
							updateFilesToUpload={setRegistrationFiles}
						/>
					</div>
				) : (
					<ul className="vehicle-registration-entries">
						{vehicleRegistrationsObjectToUse.map((vehicleRegistration, key) => (
							<li
								className="vehicle-registration-entry exemptions-input-flex-container"
								key={key}>
								<TextField
									required
									label="Vehicle Registration Tag Number"
									placeholder="Vehicle Registration Tag Number"
									containerClassName="exemptions-card-input"
									value={vehicleRegistration.registration_id ?? ""}
									maxLength={8}
									onChange={e =>
										updateVehicleRegistration(key, {
											registration_id: e.target.value,
										})
									}
								/>
								{flowId === "GEORGIA_EXEMPTION" && (
									<TextField
										required
										label="County Where Registered"
										placeholder="County Where Registered"
										containerClassName="exemptions-card-input"
										value={vehicleRegistration.county ?? ""}
										onChange={e =>
											updateVehicleRegistration(key, { county: e.target.value })
										}
									/>
								)}
								{flowId === "GEORGIA_EXEMPTION" && (
									<div
										className="remove-vehicle-registration-entry"
										onClick={() => removeVehicleRegistration(key)}>
										<CrossReverse />
									</div>
								)}
							</li>
						))}
					</ul>
				)}

				{flowId === "GEORGIA_EXEMPTION" && details?.county !== "Fulton" && (
					<Expand
						className="mx-auto mt-3"
						onClick={addAnotherVehicleRegistration}>
						Add {vehicleRegistrationsObjectToUse.length < 1 ? "A" : "Another"}{" "}
						Vehicle
					</Expand>
				)}
			</div>
			<ButtonV2 size="large" disabled={buttonDisabled} type="submit">
				{details?.county === "Fulton"
					? "I've uploaded all vehicle registrations"
					: "Continue"}
			</ButtonV2>
			{details?.county !== "Fulton" && (
				<ButtonV2
					type="button"
					variant="primary-outline"
					className="mx-auto mt-4"
					onClick={() => skipVehicleRegistration()}>
					No Vehicles Owned
				</ButtonV2>
			)}
		</form>
	);
};

export default VehicleRegistrations;
